/**
 * MIN-292 — thread wiring moves behind the factory.
 *
 * The per-thread wiring both apps used to duplicate: resolving a thread's
 * shared conversation, building its transport (auth + base URL + route
 * context read at send time, not build time), folding the server's history
 * into it exactly once, and invalidating the caches a finished reply
 * changed. Internal seam — apps reach it through `useThreadChat` (the React
 * hook) or the barrel's `resolveThreadConversation`/`hydrateThreadConversation`
 * re-exports, never by importing this module directly.
 *
 * No React here: identity/lifetime lives in `./registry`, this module only
 * adds what a thread's `Chat` needs to talk to the engine and to render a
 * reloaded history the same way a live stream would.
 */
import { Chat } from "@ai-sdk/react";
import type { QueryClient } from "@tanstack/react-query";
import { DefaultChatTransport, type UIMessage } from "ai";
import type { ChatRouteContext } from "./chat-context";
import type { ChatAuth } from "./chat-fetch";
import { makeChatFetch } from "./chat-fetch";
import type { ChatMessage, EngineMessage } from "./message";
import { chatMessageQueries, chatThreadQueries } from "./queries";
import { getThreadChat, hydrateThreadChat } from "./registry";
import type { ThreadId } from "./thread";
import { turnFromHistory, turnToUiMessage } from "./turn";

export interface ThreadConversationDeps {
  auth: ChatAuth;
  baseUrl: string;
  queryClient: QueryClient;
  /** Read at send time, not build time — the same closure every call, so a
   *  navigation between building the conversation and sending is visible. */
  getRouteContext: () => ChatRouteContext;
}

/** The shape `prepareSendMessagesRequest` hands a transport. */
interface SendRequestInput {
  id: string;
  messages: UIMessage[];
  trigger: string;
  messageId: string | undefined;
  body?: Record<string, unknown>;
}

/**
 * Builds the request body for one send: the SDK's own fields (trigger/id/
 * messageId — `build_run_input` needs them) plus the clinician's route,
 * read fresh through `getRouteContext` right now. Absent route params are
 * omitted by `buildChatContext` upstream, never sent as empty strings.
 */
export function buildSendRequestBody(
  input: SendRequestInput,
  getRouteContext: () => ChatRouteContext,
): { body: Record<string, unknown> } {
  const { id, messages, trigger, messageId, body } = input;
  return { body: { ...body, id, messages, trigger, messageId, context: getRouteContext() } };
}

/**
 * How long after a reply finishes to re-read the thread list, catching the
 * engine's background title (MIN-316 finding 5c). The titler is fire-and-forget
 * (`asyncio.create_task`) and costs a further LLM round-trip *after* the reply
 * completes, so a single invalidation at `onFinish` reliably reads the list back
 * before the name exists. `apps/provider` used this same 3s beat before the
 * consolidation; it was dropped in the move.
 */
export const THREAD_TITLE_SETTLE_MS = 3000;

/**
 * Invalidates what a finished reply changed: that thread's history, and the
 * thread list (the engine names a fresh thread in the background after its
 * first exchange) — the list twice, once now and once after
 * `THREAD_TITLE_SETTLE_MS`, because the name arrives after the reply does.
 *
 * Returns a cancel function for the deferred pass, so a caller that is tearing
 * down (a test, an unmounting surface) can drop it.
 */
export function invalidateAfterReply(
  queryClient: QueryClient,
  auth: ChatAuth,
  baseUrl: string,
  threadId: ThreadId,
  settleMs: number = THREAD_TITLE_SETTLE_MS,
): () => void {
  queryClient.invalidateQueries({ queryKey: chatMessageQueries.byThread(auth, baseUrl, threadId).queryKey });
  queryClient.invalidateQueries({ queryKey: chatThreadQueries.all() });

  const timer = setTimeout(() => {
    queryClient.invalidateQueries({ queryKey: chatThreadQueries.all() });
  }, settleMs);

  return () => clearTimeout(timer);
}

/**
 * Resolves a thread's shared `Chat`, building its transport on a cache miss.
 * Every other surface asking for the same `threadId` gets this exact
 * instance back (see `./registry`), so a reply streaming into one is visible
 * from every other.
 */
export function resolveThreadConversation(threadId: ThreadId, deps: ThreadConversationDeps): Chat<UIMessage> {
  const { auth, baseUrl, queryClient, getRouteContext } = deps;

  return getThreadChat(
    threadId,
    () =>
      new Chat<UIMessage>({
        id: `thread-${threadId}`,
        transport: new DefaultChatTransport({
          api: `${baseUrl}/chat/threads/${threadId}/messages`,
          // The SDK types this as `typeof fetch`, which under `@types/bun` carries
          // statics (`preconnect`) a plain wrapper cannot have. It is only ever
          // called, so the call signature is all that matters here. `api` above
          // is already the absolute URL the transport calls `fetch` with, so
          // this wraps it with auth + retry over an empty base.
          fetch: makeChatFetch(auth, "") as typeof fetch,
          prepareSendMessagesRequest: ({ id, messages, trigger, messageId, body }) =>
            buildSendRequestBody({ id, messages, trigger, messageId, body }, getRouteContext),
        }),
        onFinish: () => {
          invalidateAfterReply(queryClient, auth, baseUrl, threadId);
        },
      }),
  );
}

/**
 * A persisted `ChatMessage`, reshaped as the `EngineMessage` `turnFromHistory`
 * consumes — the render path's thin entry point into the package's one `Turn`
 * builder (MIN-292 review item 1). `ChatMessage` already carries every field
 * `EngineMessage` does (it is `toChatMessage`'s output), just with the
 * package's own field names; this only renames them back.
 */
function toEngineMessage(message: ChatMessage): EngineMessage {
  return {
    id: message.id,
    role: message.role,
    content: message.content,
    tool_calls: message.toolCalls,
    reasoning: message.reasoning,
    usage: message.usage
      ? {
          input_tokens: message.usage.inputTokens,
          output_tokens: message.usage.outputTokens,
          requests: message.usage.requests,
        }
      : undefined,
    interrupted: message.interrupted,
  };
}

/**
 * Folds the server's persisted history into a thread's `Chat`, once. A
 * second call (a duplicate fetch, a late refetch) is a no-op — see
 * `./registry`'s `hydrateThreadChat`, which this delegates to: an
 * already-hydrated thread, or one already holding live/local messages,
 * is left alone so a stale seed can never overwrite a stream in progress.
 *
 * Routes through `turnFromHistory`/`turnToUiMessage` (`./turn`) — the same
 * builder `createAgentChat`'s `readTranscript` uses — so this is the
 * package's one `ChatMessage`/`EngineMessage` → `Turn` → render path, not a
 * second mapper duplicating it (MIN-292 review item 1).
 */
export function hydrateThreadConversation(threadId: ThreadId, history: ChatMessage[]): boolean {
  return hydrateThreadChat(threadId, history.map(toEngineMessage).map(turnFromHistory).map(turnToUiMessage));
}
